import { useState, useSyncExternalStore } from "react";
import { EyeOff } from "lucide-react";
import { useAppDispatch } from "@/app/hooks";
import { ROUTE_PATHS } from "@/config/routes";
import { endAdminImpersonation } from "@/lib/auth/endAdminImpersonation";
import { showApiError } from "@/lib/utils/notifications";
import { cn } from "@lib/utils/helpers";

interface ImpersonationStopRowProps {
  /** Extra classes on the outer row (e.g. margin below before action buttons). */
  className?: string;
  /** `bar` = full-width strip; `inline` = under card title; `pageHeader` = compact button only (inside blue PageHeader). */
  variant?: "bar" | "inline" | "pageHeader";
  /** Optional wrapper test id (e.g. dashboard global bar). */
  dataTestId?: string;
  /**
   * When ending impersonation, pass applicant-style redirect fallback (same as StopImpersonateButton).
   * Use on Help/About and similar when return path may be generic `/dashboard`.
   */
  useApplicantImpersonationFallback?: boolean;
}

const IMPERSONATION_STORAGE_EVENT = "impersonation-storage-change";

function readImpersonationSnapshot(): string {
  if (typeof window === "undefined") return "";

  try {
    const adminToken = localStorage.getItem("admin_token");
    const flag = localStorage.getItem("impersonate");
    return adminToken || flag || "";
  } catch {
    return "";
  }
}

function subscribeToImpersonationStorage(onStoreChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === "impersonate" ||
      event.key === "admin_token" ||
      event.key === null
    ) {
      onStoreChange();
    }
  };

  window.addEventListener("storage", onStorage);
  window.addEventListener(IMPERSONATION_STORAGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(IMPERSONATION_STORAGE_EVENT, onStoreChange);
  };
}

/**
 * Shared “Stop impersonating” control (Angular `layout.endImpersonate` sequence).
 * Use `variant="pageHeader"` inside the blue PageHeader; `inline` inside white cards; `bar` for legacy full-width strip.
 */
export function ImpersonationStopRow({
  className,
  variant = "inline",
  dataTestId,
  useApplicantImpersonationFallback = false,
}: ImpersonationStopRowProps) {
  const dispatch = useAppDispatch();
  const impersonationSnapshot = useSyncExternalStore(
    subscribeToImpersonationStorage,
    readImpersonationSnapshot,
    () => "",
  );
  const [busy, setBusy] = useState(false);
  const visible = Boolean(impersonationSnapshot);

  if (!visible) return null;

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const originalApplicantId = localStorage.getItem("impersonated_applicant_id");
      const redirect = await endAdminImpersonation({
        dispatch,
        ...(useApplicantImpersonationFallback
          ? {
              applicantFallback: {
                originalApplicantId,
                applicationsListPath: ROUTE_PATHS.DASHBOARD_APPLICATIONS,
              },
            }
          : {}),
      });
      window.location.href = redirect;
    } catch (e) {
      showApiError(e);
      setBusy(false);
    }
  };

  const isBar = variant === "bar";
  const isPageHeader = variant === "pageHeader";

  const buttonClass = isPageHeader
    ? "inline-flex h-8 max-h-8 shrink-0 items-center gap-1.5 rounded-md bg-[rgb(21,151,255)] px-2.5 text-xs font-semibold text-white shadow-sm transition enabled:hover:brightness-95 enabled:active:brightness-90 disabled:cursor-not-allowed disabled:opacity-60 sm:gap-2 sm:px-3 sm:text-sm"
    : "inline-flex items-center gap-2 rounded-md bg-[rgb(21,151,255)] px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition enabled:hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60";

  if (isPageHeader) {
    return (
      <button
        type="button"
        data-testid={dataTestId ?? "dashboard-page-header-stop-impersonate"}
        onClick={() => void handleClick()}
        disabled={busy}
        className={buttonClass}
      >
        <EyeOff className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
        <span className="max-w-[9rem] truncate sm:max-w-none">
          {busy ? "Stopping…" : "Stop impersonating"}
        </span>
      </button>
    );
  }

  return (
    <div
      data-testid={dataTestId ?? "impersonation-stop-row"}
      className={cn(
        isBar
          ? "sticky top-0 z-[996] flex flex-shrink-0 items-center justify-end border-b border-gray-200 bg-white px-3 py-2 sm:px-4 sm:py-2.5 shadow-sm"
          : "flex w-full flex-wrap items-center justify-end gap-2 pb-3 mb-3 border-b border-slate-200/90",
        className,
      )}
    >
      <button
        type="button"
        data-testid="impersonation-stop-button"
        onClick={() => void handleClick()}
        disabled={busy}
        className={buttonClass}
      >
        <EyeOff className="h-4 w-4 shrink-0" aria-hidden />
        {busy ? "Stopping…" : "Stop impersonating"}
      </button>
    </div>
  );
}
